import {FC, useMemo} from "react";
import {Badge, Button, Container, Dropdown, Nav, Navbar, NavDropdown} from "react-bootstrap";
import rightRentalLogo from "../../../assets/images/right_rental_logo.png";
import redDevilLogo from "../../../assets/images/reddevil-logo.png";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCalendarAlt,
    faChampagneGlasses,
    faClock,
    faDollar,
    faFaceFrown,
    faGasPump,
    faLineChart,
    faMoneyBills,
    faPeopleGroup,
    faSignOut,
    faTruck
} from "@fortawesome/free-solid-svg-icons";
import {appAuth} from "../../../index";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    departmentNames,
    RRDepartmentType,
    selectCurrentEmployee,
    selectCurrentUser,
    selectDepartment,
    setApprovePPHPopupVisible,
    setDepartment,
    setEmployeesPopupVisible,
    setEventsPopupVisible,
    setLogisticsPopupVisible
} from "../../../features/AppStateSlice";
import {selectAllEmployees} from "../../../features/AppDataSlice";
import {canApproveRateChanges} from "../../constants/approveRateChangeUsers";

interface Props {}

export const EVENTS_PATH = "/events";
export const EMPLOYEES_PATH = "/employees";
export const TRUCKS_PATH = "/trucks";
export const FUEL_INVOICES_PATH = "/transport/fuel-invoices";
export const APPROVE_PPH_PATH = "/employees/approve";

const TRANSPORT_ONLY_PATHS = new Set([
    FUEL_INVOICES_PATH,
    "/reports/schedule",
    "/reports/km-hours",
    "/reports/hours-discrepancies",
    "/reports/roadbooks",
    "/reports/driver-change-log",
    "/reports/event-costs",
]);

const AppHeader: FC<Props> = () => {
    const dispatch = useAppDispatch();

    function userSignOut() {
        appAuth.signOut();
        window.location.href = window.location.href;
    }

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const employees = useAppSelector(selectAllEmployees);
    const department = useAppSelector(selectDepartment);
    const departmentName = departmentNames[department];
    const appLogo = department === "catering" ? redDevilLogo : rightRentalLogo;
    const canManageRateChanges = canApproveRateChanges(currentUser?.id);
    const canSeePayRateChangeReport = ["Qd3l20aHWmEKYoQ9ycgl", "DRulMuqC9vM7tud45ngd", "Ff1CPQ37aSkDOH4GksMC", "FEWMnZtRGGu7EGDoWCZA"].includes(currentUser?.id ?? "");
    const canOpenLogisticsScheduleReport = department === "logistics" &&
        currentEmployee?.isLogistics === true &&
        currentEmployee.canSeeLogisticsScheduleReport === true;

    function changeDepartment(nextDepartment: RRDepartmentType) {
        dispatch(setDepartment(nextDepartment));
        // Транспортните страници нямат вариант за друг отдел. Общите справки
        // запазват адреса си, а от транспортна страница се отваря новият календар.
        if (nextDepartment !== "logistics" && TRANSPORT_ONLY_PATHS.has(location.pathname)) {
            navigate("/");
        }
    }

    if (currentEmployee) {
        const firstAvailableDepartment = currentEmployee.isMontage ? "montage" :
            (currentEmployee.isCatering ? "catering" :
                (currentEmployee.isStore ? "store" : (currentEmployee.isLogistics ? "logistics" : "")));

        if (department === "") {
            dispatch(setDepartment(firstAvailableDepartment));
        } else {
            if (!currentEmployee.isMontage && department === "montage") {
                dispatch(setDepartment(firstAvailableDepartment));
            }
            if (!currentEmployee.isCatering && department === "catering") {
                dispatch(setDepartment(firstAvailableDepartment));
            }
            if (!currentEmployee.isStore && department === "store") {
                dispatch(setDepartment(firstAvailableDepartment));
            }
            if (!currentEmployee.isLogistics && department === "logistics") {
                dispatch(setDepartment(firstAvailableDepartment));
            }
        }
    }

    const needApprovementCnt = useMemo(() => {
        let cnt = 0;
        employees?.forEach(e => {
            const isInActiveDepartment = !!e && (
                (department === "montage" && e.isMontage) ||
                (department === "catering" && e.isCatering) ||
                (department === "store" && e.isStore) ||
                (department === "logistics" && e.isLogistics)
            );

            if (isInActiveDepartment && e.empl_pph?.length) {
                e.empl_pph.forEach((p, idx) => {
                    if (idx > 0 && !p.isApproved) {
                        cnt++;
                    }
                });
            }
        });
        return cnt;
    }, [department, employees]);

    return <Navbar fixed="top" className={"app-header"}>
        <Container fluid>
            <Navbar.Brand href="/">
                <img
                    src={appLogo}
                    height="40"
                    className="d-inline-block align-top"
                    alt="Лого"
                />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="navbarScroll"/>
            <Navbar.Collapse id="navbarScroll">
                <Nav
                    activeKey={location.pathname}
                    className="me-auto my-2 my-lg-0"
                    style={{maxHeight: "100px"}}
                    navbarScroll
                >
                    <Nav.Link as={Link} to={"/"} eventKey={"/"}>
                        <FontAwesomeIcon icon={faCalendarAlt}/> Календар {departmentName.toUpperCase()}
                    </Nav.Link>

                    <NavDropdown
                        disabled={currentEmployee?.isAdmin === false && !canOpenLogisticsScheduleReport}
                        title={<><FontAwesomeIcon icon={faLineChart}/> Справки и отчети</>}
                    >
                        {
                            canOpenLogisticsScheduleReport &&
                            <NavDropdown.Item as={Link} to={"/reports/schedule"} eventKey={"/reports/schedule"}>
                                <FontAwesomeIcon icon={faCalendarAlt}/> График
                            </NavDropdown.Item>
                        }

                        {
                            currentEmployee?.isAdmin && <>
                                <NavDropdown.Item as={Link} to={"/reports/time"} eventKey={"/reports/time"}>
                                    <FontAwesomeIcon icon={faClock}/> Изработени часове
                                </NavDropdown.Item>
                                {
                                    department === "logistics" &&
                                    <NavDropdown.Item as={Link} to={"/reports/km-hours"} eventKey={"/reports/km-hours"}>
                                        <FontAwesomeIcon icon={faClock}/> Справка за км и часове
                                    </NavDropdown.Item>
                                }
                                {
                                    department === "logistics" &&
                                    <NavDropdown.Item as={Link} to={"/reports/hours-discrepancies"} eventKey={"/reports/hours-discrepancies"}>
                                        <FontAwesomeIcon icon={faClock}/> Разминавания в часове
                                    </NavDropdown.Item>
                                }
                                {
                                    department === "logistics" &&
                                    <NavDropdown.Item as={Link} to={"/reports/roadbooks"} eventKey={"/reports/roadbooks"}>
                                        <FontAwesomeIcon icon={faTruck}/> Пътни книжки
                                    </NavDropdown.Item>
                                }
                                {
                                    department === "logistics" &&
                                    <NavDropdown.Item as={Link} to={"/reports/driver-change-log"} eventKey={"/reports/driver-change-log"}>
                                        <FontAwesomeIcon icon={faPeopleGroup}/> Лог промени в транспорт
                                    </NavDropdown.Item>
                                }
                                {
                                    (department === "logistics" && (currentEmployee?.isSuperAdmin || (currentEmployee?.isAdmin && currentEmployee?.canSeePayment))) &&
                                    <NavDropdown.Item as={Link} to={"/reports/event-costs"} eventKey={"/reports/event-costs"}>
                                        <FontAwesomeIcon icon={faMoneyBills}/> Разход по събития
                                    </NavDropdown.Item>
                                }
                                {
                                    department !== "logistics" &&
                                    <>
                                        <NavDropdown.Item as={Link} to={"/reports/calendar-change-log"} eventKey={"/reports/calendar-change-log"}>
                                            <FontAwesomeIcon icon={faPeopleGroup}/> Лог промени
                                        </NavDropdown.Item>
                                        <NavDropdown.Item as={Link} to={"/reports/negative-rating"} eventKey={"/reports/negative-rating"}>
                                            <FontAwesomeIcon icon={faFaceFrown}/> Негативни оценки
                                        </NavDropdown.Item>
                                        {
                                            canSeePayRateChangeReport &&
                                            <NavDropdown.Item as={Link} to={"/reports/timeduplicates"} eventKey={"/reports/timeduplicates"}>
                                                <FontAwesomeIcon icon={faCalendarAlt}/> Застъпване на часове
                                            </NavDropdown.Item>
                                        }
                                    </>
                                }
                                {
                                    canSeePayRateChangeReport &&
                                    <NavDropdown.Item as={Link} to={"/reports/payratechanges"} eventKey={"/reports/payratechanges"}>
                                        <FontAwesomeIcon icon={faMoneyBills}/> Промяна на заплащането
                                    </NavDropdown.Item>
                                }
                            </>
                        }
                    </NavDropdown>

                    <Nav.Link
                        eventKey={EVENTS_PATH}
                        onClick={() => dispatch(setEventsPopupVisible(true))}
                        disabled={currentEmployee?.isAdmin === false}
                    >
                        <FontAwesomeIcon icon={faChampagneGlasses}/> Събития
                    </Nav.Link>
                    <Nav.Link
                        eventKey={EMPLOYEES_PATH}
                        disabled={currentEmployee?.isSuperAdmin === false}
                        onClick={() => dispatch(setEmployeesPopupVisible(true))}
                    >
                        <FontAwesomeIcon icon={faPeopleGroup}/> Служители
                    </Nav.Link>
                    {
                        currentEmployee?.isLogistics && department === "logistics" &&
                        <NavDropdown title={<><FontAwesomeIcon icon={faTruck}/> Транспорт</>}>
                            <NavDropdown.Item as={Link} to={FUEL_INVOICES_PATH} eventKey={FUEL_INVOICES_PATH}>
                                <FontAwesomeIcon icon={faGasPump}/> Зареждания с гориво
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                eventKey={TRUCKS_PATH}
                                disabled={currentEmployee?.isSuperAdmin === false}
                                onClick={() => dispatch(setLogisticsPopupVisible(true))}
                            >
                                <FontAwesomeIcon icon={faTruck}/> Превозни средства
                            </NavDropdown.Item>
                        </NavDropdown>
                    }
                    {
                        canManageRateChanges &&
                        <Nav.Link
                            eventKey={APPROVE_PPH_PATH}
                            disabled={needApprovementCnt === 0}
                            onClick={() => dispatch(setApprovePPHPopupVisible(true))}
                        >
                            <FontAwesomeIcon icon={faDollar}/> Променени ставки <Badge bg={"danger"}>{needApprovementCnt}</Badge>
                        </Nav.Link>
                    }
                </Nav>
                <div className="d-flex">
                    <span className={"text-uppercase fw-bold"} style={{height: "40px"}}>
                        <Dropdown className={"me-2"}>
                            <Dropdown.Toggle
                                variant="dark"
                                disabled={currentEmployee?.isAdmin === false}
                                style={{height: "40px"}}
                            >
                                <span className={"text-uppercase fw-bold"} style={{color: "wheat"}}>
                                    {departmentName}&nbsp;&nbsp;
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant={"dark"}>
                                {
                                    currentEmployee?.isMontage &&
                                    <Dropdown.Item onClick={() => changeDepartment("montage")}>
                                        <span className={"text-uppercase fw-bold lh-2em"} style={{color: "wheat"}}>
                                            {departmentNames.montage}
                                        </span>
                                    </Dropdown.Item>
                                }
                                {
                                    currentEmployee?.isCatering &&
                                    <Dropdown.Item onClick={() => changeDepartment("catering")}>
                                        <span className={"text-uppercase fw-bold lh-2em"} style={{color: "wheat"}}>
                                            {departmentNames.catering}
                                        </span>
                                    </Dropdown.Item>
                                }
                                {
                                    currentEmployee?.isStore &&
                                    <Dropdown.Item onClick={() => changeDepartment("store")}>
                                        <span className={"text-uppercase fw-bold lh-2em"} style={{color: "wheat"}}>
                                            {departmentNames.store}
                                        </span>
                                    </Dropdown.Item>
                                }
                                {
                                    currentEmployee?.isLogistics &&
                                    <Dropdown.Item onClick={() => changeDepartment("logistics")}>
                                        <span className={"text-uppercase fw-bold lh-2em"} style={{color: "wheat"}}>
                                            {departmentNames.logistics}
                                        </span>
                                    </Dropdown.Item>
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </span>

                    <span className={"header-user"}>
                        {currentEmployee?.name || currentEmployee?.email || currentUser?.name || currentUser?.email}
                    </span>
                    <Button variant="outline-danger" size={"sm"} className={"ms-2"} onClick={userSignOut}>
                        <FontAwesomeIcon icon={faSignOut}/>
                    </Button>
                </div>
            </Navbar.Collapse>
        </Container>
    </Navbar>;
};

export default AppHeader;
